import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import { useField } from 'formik'
import get from 'lodash/get'
import MenuItem from '@mui/material/MenuItem'
import MuiSelectWrapper from '../../../../../common/MuiSelect'
import { selectConfig } from '../../../../../../../services/config/redux/selectors'

/**
 * Content policy AI subscription
 */
export const CONTENT_POLICY_OPTION_SUB_AI = 200

/**
 * Content policy basic subscription
 */
export const CONTENT_POLICY_OPTION_SUB_MONTHLY = 253

/**
 * Content policy annual subscription
 */
export const CONTENT_POLICY_OPTION_SUB_ANNUAL = 280

/**
 * Content policy premium subscription
 */
export const CONTENT_POLICY_OPTION_SUB_LIVE_ACCESS = 290

/**
 * Content policy Yoga International basic subscription
 */
export const CONTENT_POLICY_OPTION_SUB_YI_BASIC = 254

/**
 * Content policy Yoga International premium subscription
 */
export const CONTENT_POLICY_OPTION_SUB_YI_PREMIUM = 300

/**
 * Content policy alacarte
 */
export const CONTENT_POLICY_OPTION_ALACARTE = 222

export const CONTENT_SET_LABELS = {
  [CONTENT_POLICY_OPTION_SUB_AI]: 'AI Only',
  [CONTENT_POLICY_OPTION_SUB_MONTHLY]: 'Basic offering (monthly)',
  [CONTENT_POLICY_OPTION_SUB_ANNUAL]: 'Basic offering (annual)',
  [CONTENT_POLICY_OPTION_SUB_LIVE_ACCESS]: 'All-access offering',
  [CONTENT_POLICY_OPTION_SUB_YI_BASIC]: 'Yoga International - Basic',
  [CONTENT_POLICY_OPTION_SUB_YI_PREMIUM]: 'Yoga International - Premium',
  [CONTENT_POLICY_OPTION_ALACARTE]: 'Alacarte',
}

/**
 * Component used to display a Content Id Select
 * @param {Object} props
 * @param {Object} props.userEntitlement
 * @param {Number} props.index index of userEntitlement in Formik FieldArray
 * @param {Boolean} props.isEditing is current userEntitlement edit now
 * @returns {import('react').ReactElement}
 */
const ContentSetIdSelect = ({ userEntitlement, index, isEditing }) => {
  const fieldName = `user_entitlements.${index}.content_set_id`
  const [field, , helpers] = useField({ name: fieldName })
  const { name, value } = field
  const { setValue } = helpers
  const [selectedValue, setSelectedValue] = useState(value || '')

  const config = useSelector(selectConfig)

  // Keep local state in sync with formik values
  useEffect(() => {
    setSelectedValue(value || '')
  }, [value])

  const handleSelectChange = (event) => {
    const newValue = event.target.value
    setSelectedValue(newValue)
    setValue(newValue)
  }

  const enabledContentSets =
    config.getIn(['data', 'userEntitlements', 'enabledContentSets']).toJS() || []
  const contentSetId = get(userEntitlement, 'content_set_id')
  const contentSetFormatted = CONTENT_SET_LABELS[contentSetId] || `Offering ${contentSetId}`

  if (!isEditing) {
    return (
      <div className="user-entitlements__entitlement-item">
        <dl>
          <dt>Entitled to</dt>
          <dd>{contentSetFormatted}</dd>
        </dl>
      </div>
    )
  }

  return (
    <div className="user-entitlements__entitlement-item">
      <dl>
        <dt>Entitled to</dt>
        <dd>
          <MuiSelectWrapper
            name={name}
            onChange={handleSelectChange}
            value={selectedValue}
            className="user-entitlements__select--content-set-id"
            required
          >
            {enabledContentSets.map((csId) => {
              const label = CONTENT_SET_LABELS[csId] || `Offering ${csId}`
              return (
                <MenuItem key={csId} value={csId}>
                  {label}
                </MenuItem>
              )
            })}
          </MuiSelectWrapper>
        </dd>
      </dl>
    </div>
  )
}

ContentSetIdSelect.propTypes = {
  userEntitlement: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  isEditing: PropTypes.bool,
}

export default ContentSetIdSelect
